import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@glu/theming';
import moment from 'moment';
import padStart from 'lodash/padStart';
import { connect } from 'react-redux';
import ExtendSession from 'components/sessionExpiration/extendSession';
import jsCookie from 'js-cookie';
import { JSSClasses } from '../../proptypes/jss-prop-types';
import styles from './SessionExpiration.styles';
import withLocale from '../withLocale/withLocale';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';
import { extendSession, getSessionTimeRemaining } from 'api/user/service';
import { logout as logoutAction } from 'logic/store/authentication/actions';
import PopupWindow from './PopupWindow';
import ExpirationView from './ExpirationView';
import { STORAGE_KEYS, ONE_CALCULATED_SECOND_IN_A_DAY } from 'constants.js';

let sessionWorker;
function setWebWorker(onMessageListener) {
  sessionWorker = new Worker('/workers/timer.js');

  sessionWorker.addEventListener('message', onMessageListener);
}

const windowConfig = {
  features: { width: 480, height: 340 }
};

const THREE_SECONDS = 3000;
const ONE_SECOND = 1000;

const refreshCookie = () => {
  const tabToken = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  const token = jsCookie.get(STORAGE_KEYS.TOKEN) || tabToken;

  if (!token) {
    return;
  }

  if (!tabToken) {
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  jsCookie.set(STORAGE_KEYS.TOKEN, token, {path: '/', expires: ONE_CALCULATED_SECOND_IN_A_DAY});
};

export const ExpirationWindowComponent = (props) => {
  const { isApplicationTabHidden } = props;
  const subscriptions = {
    extendSession: null,
    sessionTimeRemaining: null
  };
  const [show, setShow] = useState(false);
  const [sessionTime, setSessionTime] = useState(null);
  const [sending, setSending] = useState(false);
  const [time, setTime] = useState(null);
  const [sessionExtendDetected, setExtendSession] = useState(moment().valueOf());

  const checkExpiry = ({sessionTimeRemaining, inactivityWarningLeadTime}) => {
    if (sessionTimeRemaining <= 0) {
      logout({ isSessionExpired: true });
    } else if (sending) {
      setShow(false);
    } else {
      const showExpirationWindow = inactivityWarningLeadTime <= THREE_SECONDS;
      setShow(showExpirationWindow);

      if (showExpirationWindow) {
        const duration = moment.duration(sessionTimeRemaining);
        const minutes = padStart(duration.get('minutes'), 2, '0');
        const seconds = padStart(duration.get('seconds'), 2, '0');

        setTime(`${minutes}:${seconds}`);
      }
    }

  };

  const logout = (params) => {
    setSending(true);
    props.logout(params);
    props.onLogout();
  };

  const resume = () => {
    setSending(true);
    sessionWorker.postMessage({ action: 'stop' }); // kill worker to stop producing events, and restart again in useEffect

    subscriptions.extendSession = extendSession().subscribe(({ success }) => {
      setShow(false);
      setSending(false);

      if (success) {
        setExtendSession(moment().valueOf());
      }

      if (!success) {
        logout({ isSessionExpired: true });
      }
    }, logout);
  };

  const onMessageListener = ({ data: { sessionTime } }) => {
    const sessionTimeData = JSON.parse(sessionTime) ?? {};

    refreshCookie();

    checkExpiry({
      sessionTimeRemaining: Math.max(
        moment(sessionTimeData.sessionTimeRemaining).diff(moment()),
        0
      ),
      inactivityWarningLeadTime: Math.max(
        moment(sessionTimeData.inactivityWarningLeadTime).diff(moment()),
        0
      )
    });
  };

  const onLocalStorageChange = () => {
    const local = JSON.parse(localStorage.getItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME));
    const session = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME));

    if (local?.sessionTimeRemaining !== session?.sessionTimeRemaining) {
      setSessionTime({
        sessionTimeRemaining: local?.sessionTimeRemaining,
        inactivityWarningLeadTime: local?.inactivityWarningLeadTime
      });
    };
  };

  useEffect(() => {
    setWebWorker(onMessageListener);
    window.addEventListener('storage', onLocalStorageChange);

    return () => {
      window.removeEventListener('storage', onLocalStorageChange);
      sessionWorker.postMessage({ action: 'stop' });
      sessionWorker.removeEventListener('message', onMessageListener);
      sessionWorker.terminate();
      unsubscribeComponent(subscriptions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isCancelled = false;

    subscriptions.sessionTimeRemaining = getSessionTimeRemaining().subscribe(({ data }) => {

      const sessionInvalid = Boolean(data?.[0]?.code);
      
      if (sessionInvalid)
        return {
          sessionTimeRemaining: moment(),
          inactivityWarningLeadTime: moment()
        };

      const serverBuffer = moment().add(data.sessionTimeRemaining - 5, 'seconds');

      const updatedTime = {
        sessionTimeRemaining: serverBuffer,
        inactivityWarningLeadTime: serverBuffer
          .clone()
          .subtract(data.inactivityWarningLeadTime, 'seconds')
      };
      
      localStorage.setItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME, JSON.stringify(updatedTime));
      sessionStorage.setItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME, JSON.stringify(updatedTime));

      if (!isCancelled) setSessionTime(updatedTime);
    });

    return () => {
      isCancelled = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionExtendDetected, setSessionTime]);

  useEffect(() => {
    if (sessionTime) {
      sessionWorker.postMessage({ action: 'stop' });
      sessionWorker.postMessage({
        action: 'start',
        timeout: ONE_SECOND,
        sessionTime: localStorage.getItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME)
      });
    }
  }, [sessionTime]);

  ExtendSession({ isExpirationWindowOpened: show, logout: props.logout, setExtendSession });

  return (
    <>
      <ExpirationView
        {...props}
        show={show}
        sending={sending}
        time={time}
        onResume={resume}
        onLogout={logout}
      />

      {isApplicationTabHidden && show && !sending && (
        <PopupWindow {...windowConfig}>
          <ExpirationView
            {...props}
            sending={sending}
            time={time}
            onResume={resume}
            onLogout={logout}
            popupMode
            show
          />
        </PopupWindow>
      )}
    </>
  );
};

ExpirationWindowComponent.propTypes = {
  classes: JSSClasses,
  locale: PropTypes.objectOf(PropTypes.string),
  logout: PropTypes.func,
  onLogout: PropTypes.func,
  isApplicationTabHidden: PropTypes.bool,
  emulation: PropTypes.bool
};

ExpirationWindowComponent.defaultProps = {
  classes: {},
  locale: {},
  logout() {},
  onLogout() {},
  isApplicationTabHidden: false,
  emulation: false
};

const ExpirationWindowStyled = withStyles(styles)(ExpirationWindowComponent);
const ExpirationWindowConnected = connect(null, { logout: logoutAction })(ExpirationWindowStyled);
export default withLocale(ExpirationWindowConnected);
